/**
 * IMPORTANT: This file is modified from the original shadcn/ui file.
 *            DO NOT OVERWRITE IT WITH THE CLI.
 */

"use client"

import { cva, type VariantProps } from "class-variance-authority"
import type * as React from "react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/utils/cn"

const badgeVariants = cva(
  "inline-flex cursor-default items-center whitespace-nowrap rounded-full border font-medium leading-normal transition-colors focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary: "border-transparent bg-muted text-foreground hover:bg-muted/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        warning: "border-transparent bg-warning text-warning-foreground hover:bg-warning/80",
        success: "border-transparent bg-success text-success-foreground hover:bg-success/80",
        outline: "text-foreground",
        primary: "border-transparent bg-purple-500 text-white",
        "primary-outline":
          "border-purple-600 text-purple-600 dark:border-purple-400 dark:text-purple-400",
      },
      size: {
        xs: "gap-1 px-1.5 text-xs [&_svg]:size-3",
        sm: "gap-1.5 px-2.5 py-0.5 text-sm [&_svg]:size-3.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "xs",
    },
  },
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  tooltip?: string
}

function Badge({ className, variant, size, tooltip, ...props }: BadgeProps) {
  if (tooltip) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={cn(badgeVariants({ variant, size }), className)} {...props} />
          </TooltipTrigger>
          <TooltipContent>
            <p>{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return <div className={cn(badgeVariants({ variant, size }), className)} {...props} />
}

export { Badge, badgeVariants }
